import * as React from 'react';
import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { AccountLink } from '@/lib/navigation/links/AccountLink';
import { Link } from '@/lib/navigation/links/Link';
import { completeOnboarding } from '@/redux/app/actions/event';
import heyThere from '../../../../assets/images/hey_there.svg';
import celebration from '../../../../assets/images/celebration.svg';
import checkmark from '../../../../assets/images/static_pages/checkmark.svg';
import { UserInfoContext } from '../user/user-info';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { Inbox } from '../inbox';

export const Onboarding = () => {
  const [showModal, setShowModal] = React.useState(false);
  const { onboarding } = useContext(UserInfoContext);

  if (!onboarding.length) return <Inbox />;
  const dispatch = useDispatch();
  const { userId } = React.useContext(UserInfoContext);
  const handleShowModal = () => setShowModal(true);
  const handleHideModal = () => setShowModal(false);
  const handleEndSetup = (e) => {
    e.preventDefault();
    handleHideModal();
    dispatch(completeOnboarding(userId));
  };

  const steps = onboarding.map((step, idx) => {
    const numberClass = `bold-600 number ${step.complete ? 'complete' : ''}`;
    const titleClass = `bold-900 ${step.complete ? 'gray opacity-50' : ''}`;
    const subtitleClass = `${step.complete ? 'gray opacity-50' : ''}`;
    const buttonClass = `button ${step.complete ? 'pale-green fit-content' : 'sea-green fit-content'}`;
    const buttonContents = <span className={buttonClass}>{step.buttonName}</span>;
    const button = step.isReactRoute === true
      ? <AccountLink path={step.buttonPath}>{buttonContents}</AccountLink>
      : <Link path={step.buttonPath}>{buttonContents}</Link>;

    return (
      <div key={`step-${idx + 1}`} className="step-container-wrap">
        <div className="step-container">
          <div className="flex">
            {step.complete
              ? (
                <div className="image-wrap">
                  <img src={checkmark} alt="checkmark" />
                </div>
              )
              : <p className={numberClass}>{idx + 1}</p>}
            <div className="inner-container">
              <h4 className={titleClass}>{step.title}</h4>
              <p className={subtitleClass}>{step.subtitle}</p>
            </div>
          </div>
          {button}
        </div>
        <hr />
      </div>
    );
  });

  const completedSteps = onboarding.filter((step) => step.complete).length;
  const isSetupComplete = completedSteps === onboarding.length;
  const stepsCompleted = `${completedSteps} of ${onboarding.length} Completed`;
  const headerContent = isSetupComplete
    ? (
      <div id="celebration-section" style={{ display: 'flex' }}>
        <img className="complete" src={celebration} alt="celebration" />
        <div className="setup-end">
          <h1>Congrats!</h1>
          <h1>You’ve completed your setup.</h1>
          <p>Now you&apos;re set.</p>
          <button
            type="button"
            className="button bright-green auto-margin md"
            onClick={handleEndSetup}
          >
            End Setup
          </button>
        </div>
      </div>
    ) : (
      <>
        <img src={heyThere} alt="hey there" />
        <h1>Welcome to Avochato!</h1>
        <p>Let’s get you set up. Follow the steps below to get up and running.</p>
        <p className="orange bold-600">{stepsCompleted}</p>
      </>
    );

  return (
    <div id="onboarding" className="avo-ui-3">
      <ConfirmationModal
        showModal={showModal}
        handleHideModal={handleHideModal}
        handleEndSetup={handleEndSetup}
        header="End Setup"
        question="Are you sure you want to end setup mode?"
        content="You can always open this back up in the support page."
        cancelText="Nevermind"
        confirmText="End Setup"
        buttonColor="bright-green"
      />
      <div className="onboarding-container">
        <div className="welcome-header">{headerContent}</div>
        {steps}
        <div className="step-container-wrap flex-end">
          <div
            id="end-setup-button"
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyPress={handleShowModal}
            onClick={isSetupComplete ? handleEndSetup : handleShowModal}
          >
            <p className="red bold-600">End Setup</p>
          </div>
        </div>
      </div>
    </div>
  );
};
